import { useCallback, useMemo, useRef, useState } from "react"

import type { PackageOp, PackageStoresOp } from "~/utils/allocationPackageOps"
import { api } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import type { PackageDeletions } from "./packageDraftChanges"
import { stageBatch } from "./packageDraftState"

type Staged = { ops: PackageOp[]; onDone?: () => void; onError?: () => void }
export type StageCallbacks = Omit<Staged, "ops">

const NO_DELETIONS: PackageDeletions = { packageIds: [], folderIds: [] }

const isStoresOp = (op: PackageOp): op is PackageStoresOp => op.kind === "setPackageStores"

/**
 * The package draft: ops waiting for Publish, in the order they were made.
 * Staged ops join the draft only once a server preview accepts them, checked
 * one batch at a time so each check builds on the last accepted draft; a
 * rejected batch is dropped with its reason. Store ticks still waiting for
 * their check are listed in `waiting` so lists can show them at once.
 * Deletions wait apart and apply last on publish.
 */
export function usePackageStaging() {
  const utils = api.useUtils()
  const [ops, setOps] = useState<PackageOp[]>([])
  const [deletions, setDeletions] = useState<PackageDeletions>(NO_DELETIONS)
  const [waiting, setWaiting] = useState<PackageStoresOp[]>([])
  const [staging, setStaging] = useState(false)
  const committed = useRef<PackageOp[]>([])
  const queue = useRef<Staged[]>([])
  const inFlight = useRef<Staged[]>([])
  const running = useRef(false)
  // Bumped by discard and publish, so a check still running for older ops is ignored.
  const generation = useRef(0)

  const syncWaiting = useCallback(
    () =>
      setWaiting(
        [...inFlight.current, ...queue.current].flatMap((item) => item.ops).filter(isStoresOp),
      ),
    [],
  )

  const check = useCallback(
    async (batch: Staged[]): Promise<{ ops: PackageOp[] } | { error: unknown }> => {
      const staged = stageBatch(
        committed.current,
        batch.flatMap((item) => item.ops),
      )
      if ("error" in staged) return { error: new Error(staged.error) }
      if (staged.ops.length === 0) return staged
      try {
        const preview = await utils.allocation.previewPackageDraft.fetch({ ops: staged.ops })
        return preview.ok ? staged : { error: new Error(preview.error) }
      } catch (error) {
        return { error }
      }
    },
    [utils],
  )

  const pump = useCallback(async () => {
    if (running.current) return
    running.current = true
    setStaging(true)
    while (queue.current.length > 0) {
      const batch = queue.current.splice(0)
      inFlight.current = batch
      const started = generation.current
      const result = await check(batch)
      if (started === generation.current) {
        inFlight.current = []
        if ("ops" in result) {
          committed.current = result.ops
          setOps(result.ops)
          for (const item of batch) item.onDone?.()
        } else {
          showErrorToast(result.error)
          for (const item of batch) item.onError?.()
        }
      }
      syncWaiting()
    }
    running.current = false
    setStaging(false)
  }, [check, syncWaiting])

  const stage = useCallback(
    (op: PackageOp | PackageOp[], callbacks: StageCallbacks = {}) => {
      queue.current.push({ ops: [op].flat(), ...callbacks })
      syncWaiting()
      void pump()
    },
    [pump, syncWaiting],
  )

  const reset = useCallback(() => {
    generation.current += 1
    queue.current = []
    inFlight.current = []
    committed.current = []
    setOps([])
    setDeletions(NO_DELETIONS)
    syncWaiting()
  }, [syncWaiting])

  /** Every staged change as Publish sends it. */
  const publishOps = useMemo(
    (): PackageOp[] => [
      ...ops,
      ...deletions.folderIds.map((folderId) => ({ kind: "deleteFolder" as const, folderId })),
      ...deletions.packageIds.map((packageId) => ({ kind: "deletePackage" as const, packageId })),
    ],
    [ops, deletions],
  )

  return useMemo(
    () => ({ ops, deletions, setDeletions, waiting, staging, stage, reset, publishOps }),
    [ops, deletions, waiting, staging, stage, reset, publishOps],
  )
}

export type PackageStaging = ReturnType<typeof usePackageStaging>
